import React from "react";
import { useLanguage } from "../context/LanguageContext";
import { content } from "../data/content";
import SectionHeading from "./SectionHeading";
import { Laptop, Users, BarChart3, Check, Globe2 } from "lucide-react";

const ICONS = { laptop: Laptop, users: Users, chart: BarChart3 };

const ForWhomSection = () => {
  const { lang } = useLanguage();
  const t = content[lang].forWhom;
  const regions = content[lang].industries.regions;

  return (
    <section id="para-quien" className="relative py-24 md:py-28 bg-narwhal-panel/40 border-y border-white/5 scroll-mt-24" data-testid="audience-markets-section">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="max-w-3xl" data-testid="audience-heading">
          <SectionHeading kicker={t.kicker} title={t.title} accent={t.titleAccent} body={t.body} testId="audience" />
        </div>

        <div className="mt-12 grid md:grid-cols-3 gap-5" data-testid="audience-profiles">
          {t.items.map((item, index) => {
            const Icon = ICONS[item.icon] || Laptop;
            return (
              <article
                key={item.n}
                data-testid={`audience-profile-${index}`}
                className="min-w-0 rounded-xl border border-white/8 bg-narwhal-dark/40 p-7 transition-colors duration-300 motion-reduce:transition-none hover:border-narwhal-teal/30"
              >
                <div className="flex items-start justify-between">
                  <span className="text-2xl font-light text-narwhal-teal/60" data-testid={`audience-profile-number-${index}`}>{item.n}</span>
                  <span className="flex h-11 w-11 items-center justify-center rounded-lg border border-narwhal-teal/20 bg-narwhal-teal/[0.06] text-narwhal-teal">
                    <Icon className="h-5 w-5" strokeWidth={1.5} aria-hidden="true" />
                  </span>
                </div>
                <h3 className="mt-6 text-lg font-medium leading-snug text-white" data-testid={`audience-profile-title-${index}`}>{item.title}</h3>
                <p className="mt-3 text-sm text-narwhal-muted leading-relaxed" data-testid={`audience-profile-description-${index}`}>{item.text}</p>
                <div className="my-6 h-px w-full bg-white/8" />
                <ul className="space-y-3" data-testid={`audience-profile-checks-${index}`}>
                  {item.checks.map((check, ci) => (
                    <li key={ci} className="flex items-center gap-3 text-sm text-white/85" data-testid={`audience-profile-check-${index}-${ci}`}>
                      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-narwhal-teal/40 bg-narwhal-teal/10 text-narwhal-teal">
                        <Check className="h-3 w-3" strokeWidth={2.5} aria-hidden="true" />
                      </span>
                      <span>{check}</span>
                    </li>
                  ))}
                </ul>
              </article>
            );
          })}
        </div>

        <div className="mt-10 flex items-center gap-3 border-t border-white/8 pt-6 text-sm text-narwhal-muted" data-testid="audience-markets-footer">
          <Globe2 className="h-4 w-4 shrink-0 text-narwhal-teal" strokeWidth={1.5} aria-hidden="true" />
          <span data-testid="audience-markets-label">{t.marketsLabel}</span>
          <span className="text-white/20">|</span>
          {regions.map((region, index) => (
            <React.Fragment key={index}>
              {index > 0 && <span className="text-white/20">|</span>}
              <span className="font-medium text-white/85" data-testid={`audience-market-name-${index}`}>{region}</span>
            </React.Fragment>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ForWhomSection;
